import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import { HttpErrorResponse } from "@angular/common/http";
import { Events, AlertController } from "ionic-angular";

import { AppConfig } from "../../app/app.config";
import { Api } from "../api/api";

import { MasterDataProvider } from "../master-data/master-data";
import { FirebasePluginProvider } from "../firebase/firebase-plugin";

import {
  ApplicationError,
  AuthenticationFailedError,
  NetworkError,
} from "../errors";

import { Me } from "../../models/user";
import { DeviceProvider } from "../../providers/device/device";
import { UserProvider } from "../user/user";
import { AllProperty } from "../../models/property";
import { UnreadNumProvider } from "../unread-num/unread-num";
import { Tracker } from "../tracker/tracker";
import { Observable } from "rxjs/Observable";
import { Withdraw } from "../../models/withdraw";

@Injectable()
export class Auth {
  private MANAGED_USER_TOKEN_KEY: string = "X-Pariv-Managed-User-Id";
  private PREVIOUS_ME_STATUS_KEY: string = "pv-previous-me-status";
  private PREVIOUS_AGE_VERIFICATION_STATUS_KEY: string =
    "pv-previous-age-verification-status";
  private me: Me;

  constructor(
    private tracker: Tracker,
    private unreadNumProvider: UnreadNumProvider,
    private userProvider: UserProvider,
    private masterDataProvider: MasterDataProvider,
    private deviceProvider: DeviceProvider,
    private firebaseProvider: FirebasePluginProvider,
    private storage: Storage,
    private api: Api,
    public alertCtrl: AlertController,
    private events: Events
  ) {}

  getMe(): Me {
    return this.me;
  }

  // ログイン・新規登録
  loginWithToken(me: Me, token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.syncTokenToHttpHeader(token).then(
        () => {
          this.login(me);
          this.setToken(token);
          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  withdraw(param: Withdraw): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userProvider.withdraw(param).subscribe(
        () => {
          this.removeToken().then(
            () => {
              this.me = undefined;
              this.api.initializeHeader();
              resolve();
            },
            (error) => {
              reject(error);
            }
          );
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  sendMailConfirmation(email: any): Observable<any> {
    let url: string = "/reset_password";
    return this.api.post(url, { email: email });
  }

  logout(): Promise<void> {
    this.me = undefined;
    this.api.initializeHeader();
    return this.removeToken();
  }

  setToken(token: string): Promise<void> {
    return this.storage.set(AppConfig.AUTH_TOKEN_KEY, token);
  }

  async getToken(): Promise<string> {
    let token: string = await this.storage.get(AppConfig.AUTH_TOKEN_KEY);
    // v1 から v2へ移行時に、ログイン情報を保持する
    // v1はlocalstorageにtokenを保持している
    // 利用は一度のみ
    if (!token) {
      token = localStorage.getItem(AppConfig.AUTH_TOKEN_KEY);
      localStorage.removeItem(AppConfig.AUTH_TOKEN_KEY);
      if (token) this.setToken(token);
    }
    return token;
  }

  removeToken(): Promise<void> {
    return this.storage.remove(AppConfig.AUTH_TOKEN_KEY);
  }

  syncTokenToHttpHeader(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.api.setHeader(AppConfig.AUTH_TOKEN_KEY, token);
      this.getManagedUser().then(
        (userId: string) => {
          if (userId != null) {
            console.log("******** managed user: " + userId);
            this.api.setHeader(this.MANAGED_USER_TOKEN_KEY, userId.toString());
          }
          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  setProperty(property: AllProperty): void {
    this.me.property = property;
    this.events.publish("update:me", this.me);
  }

  private checkEndPremium(): void {
    this.storage
      .get(this.PREVIOUS_ME_STATUS_KEY)
      .then((previousStatus: string) => {
        if (!this.me.isPremium() && previousStatus == "premium") {
          const alert = this.alertCtrl.create({
            title: "月額課金が終了いたしました",
            subTitle: "通常会員に戻ります。",
            buttons: [
              {
                text: "OK",
              },
            ],
          });
          alert.present();
        }
        this.storage.set(this.PREVIOUS_ME_STATUS_KEY, this.me.getStatus());
      });
  }

  private checkEndAgeVerification(): void {
    this.storage
      .get(this.PREVIOUS_AGE_VERIFICATION_STATUS_KEY)
      .then((previousStatus: boolean) => {
        if (this.me.isPassedAgeVerification() && !previousStatus) {
          const alert = this.alertCtrl.create({
            title: "",
            subTitle: "年齢確認の審査が完了しました",
            buttons: [
              {
                text: "OK",
              },
            ],
          });
          alert.present();
        }
        this.storage.set(
          this.PREVIOUS_AGE_VERIFICATION_STATUS_KEY,
          this.me.isPassedAgeVerification()
        );
      });
  }

  updateMe(): Promise<Me> {
    return new Promise((resolve, reject) => {
      this.userProvider.loadMe().subscribe(
        (data: Me) => {
          this.me = data;
          this.checkEndPremium();
          this.checkEndAgeVerification();
          this.events.publish("update:me", data);
          resolve(data);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  authenticate(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.getToken().then(
        (token) => {
          if (!token) return resolve(false);
          this.syncTokenToHttpHeader(token).then(
            () => {
              this.userProvider.loadMe().subscribe(
                (data: Me) => {
                  this.login(data);
                  this.checkEndPremium();
                  this.checkEndAgeVerification();
                  resolve(true);
                },
                (httpError: HttpErrorResponse) => {
                  if (httpError.error instanceof Error) {
                    // A client-side or network error occurred.
                    reject(new NetworkError());
                  } else if (httpError.status == 401) {
                    // authentication failed.
                    reject(new AuthenticationFailedError());
                  }
                  // other error.
                  reject(new ApplicationError(httpError.statusText));
                }
              );
            },
            (error) => {
              reject(error); // syncTokenToHttpHeader
            }
          );
        },
        (error) => {
          resolve(false); // getToken
        }
      );
    });
  }

  getManagedUser(): Promise<string> {
    return this.storage.get(this.MANAGED_USER_TOKEN_KEY);
  }

  setManagedUser(userId: number): Promise<any> {
    return this.storage.set(this.MANAGED_USER_TOKEN_KEY, userId);
  }

  removeManagedUser(): Promise<any> {
    return this.storage.remove(this.MANAGED_USER_TOKEN_KEY);
  }

  // 認証済みユーザーは起動時・ログイン・新規登録時に必ず通る。
  private login(me: Me): void {
    console.log("*** login with");
    console.log(me);
    this.me = me;

    this.initializeMe();
    this.tracker.update(this.me);

    this.events.subscribe("update:unread-num", () => {
      this.unreadNumProvider.load();
    });

    this.events.subscribe("platform:resume", () => {
      this.getToken().then((token) => {
        if (token) {
          this.updateMe().then(() => {
            this.initializeMe();
          });
        }
      });
    });
  }

  // 認証済みユーザーは、起動時・resume・ログイン・新規登録時に必ず通る。
  private initializeMe(): void {
    // getAllType
    // FIXME: 同期的に取ってきたいなー。今はローディングを表示している
    this.masterDataProvider.syncData();

    // 位置情報更新
    // this.geo.updateLocation();

    // permissionの取得とpush_token取得
    this.firebaseProvider
      .getTokenWithGrantPermission()
      .then((token) => {
        console.log(`The token is ${token}`);
        if (token) {
          this.deviceProvider.putToken(token);
        }
      })
      .catch((error) => {
        console.log("Error getting token", error);
      });

    // FIXME: とりあえず起動時にバッジを0に
    this.firebaseProvider.clearBadgeNum();

    // pushがきた時の設定
    this.firebaseProvider.setupOnNotification();

    this.unreadNumProvider.load();
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth/auth.ts